import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { authApi } from '../api'
import { usePartner } from '../hooks'
import { authTexts } from '../auth.texts'
import { EmailStep, OtpVerification } from '../components'

const RESEND_COOLDOWN_SECONDS = 30

type LoginStep = 'email' | 'code'

export const LoginPage = () => {
  const navigate = useNavigate()
  const { mutate: mutatePartner } = usePartner()

  const [step, setStep] = useState<LoginStep>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [serverError, setServerError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)

  // Countdown for the resend button
  useEffect(() => {
    if (cooldownSeconds <= 0) return
    const timer = setInterval(() => {
      setCooldownSeconds((s) => (s > 0 ? s - 1 : 0))
    }, 1000)
    return () => clearInterval(timer)
  }, [cooldownSeconds])

  const handleEmailSubmit = async (submittedEmail: string) => {
    setIsSubmitting(true)
    setServerError(null)
    const result = await authApi.requestCode(submittedEmail)
    setIsSubmitting(false)

    if (!result.success) {
      setServerError(result.error ?? authTexts.login.serverError)
      return
    }

    // The backend always answers success — never reveal whether the email
    // belongs to a registered partner. Proceed to the code screen normally.
    setEmail(submittedEmail)
    setCode('')
    setStep('code')
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS)
  }

  const handleVerify = async () => {
    setIsVerifying(true)
    setServerError(null)
    const result = await authApi.verifyOtp(email, code)
    setIsVerifying(false)

    if (!result.success || !result.partner) {
      setServerError(result.error ?? authTexts.otp.invalidCode)
      return
    }

    await mutatePartner(result.partner, { revalidate: false })
    navigate('/', { replace: true })
  }

  const handleResend = async () => {
    setIsResending(true)
    setServerError(null)
    const result = await authApi.resendCode(email)
    setIsResending(false)

    if (!result.success) {
      setServerError(result.error ?? authTexts.login.serverError)
      return
    }

    setCode('')
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS)
    toast.success(authTexts.otp.resendSuccess)
  }

  const handleChangeEmail = () => {
    setStep('email')
    setCode('')
    setServerError(null)
  }

  const handleCodeChange = (nextCode: string) => {
    setCode(nextCode)
    if (serverError) setServerError(null)
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-brand">
      <div className="w-full max-w-[440px] bg-background rounded-lg shadow-xl p-6 sm:p-10 animate-fade-in">
        {step === 'email' ? (
          <EmailStep
            initialEmail={email}
            onSubmit={handleEmailSubmit}
            serverError={serverError}
            isSubmitting={isSubmitting}
          />
        ) : (
          <OtpVerification
            email={email}
            code={code}
            onCodeChange={handleCodeChange}
            onSubmit={handleVerify}
            onResend={handleResend}
            onChangeEmail={handleChangeEmail}
            serverError={serverError}
            isVerifying={isVerifying}
            isResending={isResending}
            cooldownSeconds={cooldownSeconds}
          />
        )}
      </div>
    </div>
  )
}
